import { useInfiniteQuery } from '@tanstack/react-query';
import { History, TriangleAlert } from 'lucide-react';

import type { EventBody } from '@/shared/api/generated/model';
import { locale, t } from '@/shared/i18n';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/Skeleton';

import styles from './ActivityFeed.module.scss';

export interface ActivityPage {
  items: EventBody[] | null;
  next_before?: string;
  next_before_id?: string;
}

export interface Cursor {
  before: string;
  before_id: string;
}

/**
 * Лента журнала: события по дням, «Показать ещё» по курсору.
 *
 * Одна и та же для журнала сайта и журнала платформы — различается только,
 * откуда брать страницы и показывать ли, на каком сайте событие.
 */
export function ActivityFeed({
  queryKey,
  fetchPage,
  showSite = false,
}: {
  queryKey: readonly unknown[];
  fetchPage: (cursor: Cursor | undefined) => Promise<ActivityPage>;
  showSite?: boolean;
}) {
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as Cursor | undefined,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) =>
      last.next_before !== undefined && last.next_before_id !== undefined
        ? { before: last.next_before, before_id: last.next_before_id }
        : undefined,
  });

  if (query.isLoading) {
    return (
      <div className={styles['feed']}>
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </div>
    );
  }

  if (query.isError) {
    return <EmptyState icon={<TriangleAlert />} title={t.activity.failed} />;
  }

  const events = query.data?.pages.flatMap((page) => page.items ?? []) ?? [];

  if (events.length === 0) {
    return (
      <EmptyState icon={<History />} title={t.activity.empty} description={t.activity.emptyHint} />
    );
  }

  return (
    <div className={styles['feed']}>
      {byDay(events).map(([day, items]) => (
        <section key={day} className={styles['day']}>
          <h2 className={styles['day__title']}>{day}</h2>
          <ol className={styles['list']}>
            {items.map((event) => (
              <EventRow key={event.id} event={event} showSite={showSite} />
            ))}
          </ol>
        </section>
      ))}

      {query.hasNextPage && (
        <div className={styles['more']}>
          <Button
            variant="outline"
            tone="neutral"
            loading={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t.activity.more}
          </Button>
        </div>
      )}
    </div>
  );
}

function EventRow({ event, showSite }: { event: EventBody; showSite: boolean }) {
  const actor =
    event.actor === undefined
      ? t.activity.deletedUser
      : event.actor.name === ''
        ? event.actor.email
        : event.actor.name;

  const phrase = t.activity.actions[event.action] ?? event.action;
  const time = new Date(event.created_at).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className={styles['event']}>
      <time className={styles['event__time']} dateTime={event.created_at}>
        {time}
      </time>

      <div className={styles['event__body']}>
        <p className={styles['event__line']}>
          <b>{actor}</b> — {phrase}
          {event.target_label !== '' && <> «{event.target_label}»</>}
        </p>

        <div className={styles['event__meta']}>
          {showSite && event.site_name !== undefined && (
            <Badge size="sm" variant="outline" mono>
              {event.site_name}
            </Badge>
          )}
          {event.environment !== undefined && (
            <Badge size="sm" tone={event.environment === 'development' ? 'warning' : 'neutral'}>
              {event.environment === 'development' ? t.env.development : t.env.production}
            </Badge>
          )}
          <Details event={event} />
          {/* Работа от чужого имени видна в каждой строке: без этого
              действия поддержки неотличимы от действий клиента. */}
          {event.impersonator_email !== undefined && (
            <Badge size="sm" tone="accent">
              {t.activity.asSupport(event.impersonator_email)}
            </Badge>
          )}
        </div>
      </div>
    </li>
  );
}

/** Подробности, которые стоит видеть без раскрытия: роль до и после. */
function Details({ event }: { event: EventBody }) {
  const details = event.details as Record<string, unknown>;

  if (event.action === 'member.role') {
    const from = roleLabel(details['from'], details['dev_only_from']);
    const to = roleLabel(details['to'], details['dev_only_to']);

    return (
      <span className={styles['event__details']}>
        {from} → {to}
      </span>
    );
  }

  if (event.action === 'member.invite' || event.action === 'member.join') {
    return (
      <span className={styles['event__details']}>
        {roleLabel(details['role'], details['dev_only'])}
      </span>
    );
  }

  if (event.action === 'site.address' && typeof details['url'] === 'string') {
    return <span className={styles['event__details']}>{details['url']}</span>;
  }

  return null;
}

function roleLabel(role: unknown, devOnly: unknown): string {
  const name = typeof role === 'string' ? (t.team.roles[role] ?? role) : '';

  return devOnly === true ? `${name}, ${t.team.devOnlyBadge}` : name;
}

/** Группы по дням: «Сегодня», «Вчера», дальше дата. */
function byDay(events: EventBody[]): [string, EventBody[]][] {
  const groups = new Map<string, EventBody[]>();
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();

  for (const event of events) {
    const date = new Date(event.created_at);
    const key = date.toDateString();
    const label =
      key === today
        ? t.activity.today
        : key === yesterday
          ? t.activity.yesterday
          : date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });

    const group = groups.get(label) ?? [];
    group.push(event);
    groups.set(label, group);
  }

  return [...groups];
}
